import { generateColorShades, getContrastText, generateColorVariations } from './palette';
/**
 * Checks if company theme has any customizations
 */
function hasThemeCustomizations(theme) {
    if (!theme)
        return false;
    return !!(theme.primaryColor ||
        theme.secondaryColor ||
        theme.backgroundColor ||
        theme.errorColor ||
        theme.warningColor ||
        theme.successColor ||
        theme.infoColor ||
        theme.fontFamily ||
        theme.fontSize ||
        theme.fontStyle);
}
/**
 * Applies company theme customizations to the base theme options
 * If no customizations exist, returns the base theme with defaults
 */
export function applyCompanyTheme(baseTheme, companyTheme, companyId, companyName) {
    // If no theme or no customizations, return base theme with defaults
    if (!companyTheme || !hasThemeCustomizations(companyTheme)) {
        return baseTheme;
    }
    const customizedTheme = { ...baseTheme };
    // Helper to normalize color (ensure # prefix)
    const normalizeColor = (color) => {
        if (!color)
            return color;
        return color.startsWith('#') ? color : `#${color}`;
    };
    // Check if company is VDK groep
    const isVDKGroep = companyName?.toLowerCase().includes('vdk groep') ||
        companyName?.toLowerCase().includes('vdkgroep') ||
        companyName?.toLowerCase() === 'vdk groep';
    const vdkPrimaryColor = '#00B6ED'; // Sky Blue/100
    const vdkSecondaryColor = '#3192D0'; // Ocean Blue/100
    const vdkFontFamily = 'SuisseIntl';
    // Check if company has NO customizations at all
    const hasNoCustomizations = !companyTheme.primaryColor && !companyTheme.secondaryColor && !companyTheme.fontFamily;
    // Apply VDK colors only if: VDK groep OR no customizations at all
    const shouldApplyVDKColors = isVDKGroep || hasNoCustomizations;
    // Apply VDK font only if: no customizations at all
    const shouldApplyVDKFont = hasNoCustomizations;
    // Set background
    // Set background: use company backgroundColor if provided, otherwise keep base theme default
    if (customizedTheme.palette) {
        const backgroundDefault = companyTheme.backgroundColor
            ? normalizeColor(companyTheme.backgroundColor)
            : // Pure black for VDK groep
                customizedTheme.palette.background?.default || '#F9F9F9'; // Keep base theme default for others
        customizedTheme.palette = {
            ...customizedTheme.palette,
            background: {
                ...customizedTheme.palette.background,
                default: backgroundDefault
            }
        };
    }
    // Apply color customizations
    if (customizedTheme.palette) {
        // Apply VDK colors if needed (VDK groep OR both colors missing)
        if (shouldApplyVDKColors) {
            const vdkPrimaryContrastText = getContrastText(vdkPrimaryColor);
            const vdkPrimaryShades = generateColorShades(vdkPrimaryColor, vdkPrimaryContrastText);
            const vdkPrimaryVariations = generateColorVariations(vdkPrimaryColor);
            const vdkSecondaryContrastText = getContrastText(vdkSecondaryColor);
            const vdkSecondaryShades = generateColorShades(vdkSecondaryColor, vdkSecondaryContrastText);
            const vdkSecondaryVariations = generateColorVariations(vdkSecondaryColor);
            // Gradient: use fixed gradient for VDK groep
            const primaryGradient = {
                main: 'linear-gradient(135deg, #00244D 0%, #3192D0 100%)',
                bold: 'linear-gradient(135deg, #00B6ED 0%, #00244D 100%)'
            };
            customizedTheme.palette = {
                ...customizedTheme.palette,
                primary: {
                    ...customizedTheme.palette.primary,
                    ...vdkPrimaryVariations,
                    contrastText: vdkPrimaryContrastText,
                    shades: vdkPrimaryShades,
                    gradient: primaryGradient
                },
                secondary: {
                    ...customizedTheme.palette.secondary,
                    ...vdkSecondaryVariations,
                    contrastText: vdkSecondaryContrastText,
                    shades: vdkSecondaryShades
                }
            };
        }
        else {
            // Use company's custom colors if provided
            if (companyTheme.primaryColor) {
                const primaryColor = normalizeColor(companyTheme.primaryColor);
                const contrastText = getContrastText(primaryColor);
                const primaryShades = generateColorShades(primaryColor, contrastText);
                const primaryVariations = generateColorVariations(primaryColor);
                // Gradient: use the same bold gradient from base palette for all companies
                // Preserve the base theme's gradient (from PRIMARY constant in palette.ts)
                const basePrimaryGradient = baseTheme.palette?.primary?.gradient;
                const primaryGradient = {
                    main: basePrimaryGradient?.main || 'linear-gradient(135deg, #00244D 0%, #3192D0 100%)',
                    bold: basePrimaryGradient?.bold || 'linear-gradient(135deg, #00B6ED 0%, #00244D 100%)'
                };
                customizedTheme.palette = {
                    ...customizedTheme.palette,
                    primary: {
                        ...customizedTheme.palette.primary,
                        ...primaryVariations,
                        contrastText: contrastText,
                        shades: primaryShades,
                        gradient: primaryGradient
                    }
                };
            }
            if (companyTheme.secondaryColor) {
                const secondaryColor = normalizeColor(companyTheme.secondaryColor);
                const contrastText = getContrastText(secondaryColor);
                const secondaryShades = generateColorShades(secondaryColor, contrastText);
                const secondaryVariations = generateColorVariations(secondaryColor);
                customizedTheme.palette = {
                    ...customizedTheme.palette,
                    secondary: {
                        ...customizedTheme.palette.secondary,
                        ...secondaryVariations,
                        contrastText: contrastText,
                        shades: secondaryShades
                    }
                };
            }
        }
    }
    if (companyTheme.errorColor && customizedTheme.palette) {
        customizedTheme.palette = {
            ...customizedTheme.palette,
            error: {
                ...customizedTheme.palette.error,
                main: companyTheme.errorColor
            }
        };
    }
    if (companyTheme.warningColor && customizedTheme.palette) {
        customizedTheme.palette = {
            ...customizedTheme.palette,
            warning: {
                ...customizedTheme.palette.warning,
                main: companyTheme.warningColor
            }
        };
    }
    if (companyTheme.successColor && customizedTheme.palette) {
        customizedTheme.palette = {
            ...customizedTheme.palette,
            success: {
                ...customizedTheme.palette.success,
                main: companyTheme.successColor
            }
        };
    }
    if (companyTheme.infoColor && customizedTheme.palette) {
        customizedTheme.palette = {
            ...customizedTheme.palette,
            info: {
                ...customizedTheme.palette.info,
                main: companyTheme.infoColor
            }
        };
    }
    let finalFontFamily;
    if (shouldApplyVDKFont) {
        // Apply VDK font when: no font AND no colors, OR no font but has colors
        finalFontFamily = vdkFontFamily;
    }
    else if (companyTheme.fontFamily) {
        // Company has custom font
        finalFontFamily = companyTheme.fontFamily;
    }
    // When a fontFamily is determined, update ALL typography variants to use it
    if (finalFontFamily && customizedTheme.typography) {
        const fontFamily = companyTheme.fontFamily;
        const currentTypography = customizedTheme.typography;
        customizedTheme.typography = {
            ...currentTypography,
            fontFamily: finalFontFamily,
            // Update all standard typography variants to use the font
            h1: { ...(currentTypography.h1 || {}), fontFamily: finalFontFamily },
            h2: { ...(currentTypography.h2 || {}), fontFamily: finalFontFamily },
            h3: { ...(currentTypography.h3 || {}), fontFamily: finalFontFamily },
            h4: { ...(currentTypography.h4 || {}), fontFamily: finalFontFamily },
            h5: { ...(currentTypography.h5 || {}), fontFamily: finalFontFamily },
            h6: { ...(currentTypography.h6 || {}), fontFamily: finalFontFamily },
            subtitle1: { ...(currentTypography.subtitle1 || {}), fontFamily: finalFontFamily },
            subtitle2: { ...(currentTypography.subtitle2 || {}), fontFamily: finalFontFamily },
            body1: { ...(currentTypography.body1 || {}), fontFamily: finalFontFamily },
            body2: { ...(currentTypography.body2 || {}), fontFamily: finalFontFamily },
            body3: { ...(currentTypography.body3 || {}), fontFamily: finalFontFamily },
            button: { ...(currentTypography.button || {}), fontFamily: finalFontFamily },
            caption: { ...(currentTypography.caption || {}), fontFamily: finalFontFamily },
            captionOutfit: { ...(currentTypography.captionOutfit || {}), fontFamily: finalFontFamily },
            body2Outfit: { ...(currentTypography.body2Outfit || {}), fontFamily: finalFontFamily },
            subtitle1Outfit: {
                ...(currentTypography.subtitle1Outfit || {}),
                fontFamily: finalFontFamily
            },
            overline: { ...(currentTypography.overline || {}), fontFamily: finalFontFamily }
        };
    }
    if (companyTheme.fontStyle && customizedTheme.typography) {
        const fontStyle = companyTheme.fontStyle;
        const currentTypography = customizedTheme.typography;
        customizedTheme.typography = {
            ...currentTypography,
            body1: { ...(currentTypography.body1 || {}), fontStyle },
            body2: { ...(currentTypography.body2 || {}), fontStyle }
        };
    }
    // Font size adjustments can be applied through htmlFontSize
    if (companyTheme.fontSize && customizedTheme.typography) {
        const baseFontSize = parseFloat(companyTheme.fontSize);
        if (!isNaN(baseFontSize)) {
            customizedTheme.typography = {
                ...customizedTheme.typography,
                htmlFontSize: baseFontSize
            };
        }
    }
    return customizedTheme;
}
/**
 * Generates dynamic @font-face CSS rules for a given font family
 * Supports multiple font formats (woff2, ttf) with proper fallback
 */
export function generateFontFaceCSS(fontFamily) {
    // Font weights and their corresponding file suffixes
    const fontWeights = [
        { weight: 300, suffix: 'Light' },
        { weight: 400, suffix: 'Regular' },
        { weight: 500, suffix: 'Medium' },
        { weight: 600, suffix: 'SemiBold' },
        { weight: 700, suffix: 'Bold' }
    ];
    // Font formats in order of preference (modern to legacy)
    const fontFormats = [
        { ext: 'woff2', format: 'woff2' },
        { ext: 'ttf', format: 'truetype' }
    ];
    let fontFaceCSS = '';
    for (const { weight, suffix } of fontWeights) {
        // Build src attribute with all available formats for fallback
        const srcParts = [];
        for (const { ext, format } of fontFormats) {
            const fontPath = `/fonts/${fontFamily}/${fontFamily}-${suffix}.${ext}`;
            srcParts.push(`url('${fontPath}') format('${format}')`);
        }
        // Generate @font-face rule with multiple format sources
        fontFaceCSS += `@font-face {\n`;
        fontFaceCSS += `  font-family: '${fontFamily}';\n`;
        fontFaceCSS += `  src: ${srcParts.join(',\n       ')};\n`;
        fontFaceCSS += `  font-weight: ${weight};\n`;
        fontFaceCSS += `  font-style: normal;\n`;
        fontFaceCSS += `  font-display: swap;\n`;
        fontFaceCSS += `}\n\n`;
    }
    return fontFaceCSS;
}
/**
 * Injects font-face CSS into the document head
 */
export function injectFontFaceCSS(fontFamily) {
    if (!fontFamily) {
        // Remove any existing dynamic font CSS
        const existingStyle = document.getElementById('dynamic-font-faces');
        if (existingStyle) {
            existingStyle.remove();
        }
        return;
    }
    // Remove any existing dynamic font CSS
    const existingStyle = document.getElementById('dynamic-font-faces');
    if (existingStyle) {
        existingStyle.remove();
    }
    // Generate and inject new font-face CSS
    const fontFaceCSS = generateFontFaceCSS(fontFamily);
    const styleElement = document.createElement('style');
    styleElement.id = 'dynamic-font-faces';
    styleElement.textContent = fontFaceCSS;
    document.head.appendChild(styleElement);
}
/**
 * Generates a CSS variable set from company theme
 */
export function generateThemeCSSVariables(companyTheme) {
    let cssVars = ':root {\n';
    if (companyTheme.primaryColor) {
        cssVars += `  --theme-primary-color: ${companyTheme.primaryColor};\n`;
    }
    if (companyTheme.secondaryColor) {
        cssVars += `  --theme-secondary-color: ${companyTheme.secondaryColor};\n`;
    }
    if (companyTheme.backgroundColor) {
        cssVars += `  --theme-background-color: ${companyTheme.backgroundColor};\n`;
    }
    if (companyTheme.fontFamily) {
        cssVars += `  --theme-font-family: ${companyTheme.fontFamily};\n`;
    }
    if (companyTheme.fontSize) {
        cssVars += `  --theme-font-size: ${companyTheme.fontSize};\n`;
    }
    cssVars += '}\n';
    return cssVars;
}
